import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getCommercialCalibration,
  previewCommercialCalibration,
  saveCommercialCalibration,
  type CommercialCalibrationRequest,
  type CommercialPreviewRequest,
} from "@/api/commercialCalibration";
import { diagnosticKeys } from "@/hooks/useDiagnostic";
import { statusOf } from "@/lib/api-error";

export const commercialCalibrationKeys = {
  all: (projectId: string) => ["commercial-calibration", projectId] as const,
  calibration: (projectId: string) => ["commercial-calibration", projectId, "calibration"] as const,
};

/** A última versão salva, a base de hoje e se ela mudou desde então. */
export function useCommercialCalibration(projectId: string | undefined) {
  return useQuery({
    queryKey: commercialCalibrationKeys.calibration(projectId ?? ""),
    queryFn: () => getCommercialCalibration(projectId!),
    enabled: !!projectId,
  });
}

/**
 * O recálculo ao vivo, pelo motor do servidor.
 *
 * `useMutation` e não `useQuery`, como na Calibragem técnica: a tela dispara a
 * cada ajuste e o corpo é o estado inteiro dela. **Não grava nada.**
 */
export function useCommercialCalibrationPreview(projectId: string | undefined) {
  return useMutation({
    mutationFn: (body: CommercialPreviewRequest) => previewCommercialCalibration(projectId!, body),
  });
}

/**
 * Grava a versão seguinte.
 *
 * O Diagnóstico relê na hora: o card "Projeto de migração" mostra o cenário
 * marcado na última versão, e trocar a marcação tem de trocar o card (v0.3 RF-03).
 * O snapshot do Diagnóstico tem cache de 30 s e não se invalida sozinho.
 *
 * Na recusa por conflito (409: a base falta ou mudou, ou outra versão entrou no
 * meio), a calibragem relê: a tela mostra a base e a versão de agora, mantém o
 * que a pessoa digitou, e salvar de novo vai com as referências novas.
 */
export function useSaveCommercialCalibration(projectId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: CommercialCalibrationRequest) =>
      saveCommercialCalibration(projectId!, body),
    onSuccess: () =>
      Promise.all([
        client.invalidateQueries({ queryKey: commercialCalibrationKeys.all(projectId ?? "") }),
        client.invalidateQueries({ queryKey: diagnosticKeys.snapshot(projectId ?? "") }),
      ]),
    onError: (error) => {
      if (statusOf(error) !== 409) return;
      return client.invalidateQueries({
        queryKey: commercialCalibrationKeys.all(projectId ?? ""),
      });
    },
  });
}
